import { useState, useEffect, FC, useRef } from 'react';
import { Typography, Space, Switch, InputNumber } from 'antd'
import debounce from 'just-debounce-it';
import {
  MbsMarketAlertForm,
  useMbsMarketAlert,
  useUpdateMbsMarketAlert
} from '../../../../hooks/mbsMarketAlerts';
import ContentCard from '../../../Common/ContentCard';

const MbsMarketAlerts: FC = () => {
  const [mbsMarketAlertsData, setMbsMarketAlertsData] = useState<MbsMarketAlertForm | null>(null)
  const { fetch: getMbsMarketAlert, loading: isGetMbsMarketAlertLoading } = useMbsMarketAlert();
  const { fetch: updateMbsMarketAlert } = useUpdateMbsMarketAlert();

  const debouncedUpdate = useRef(
    debounce((updatedFields: MbsMarketAlertForm) => {
      updateMbsMarketAlert(updatedFields);
    }, 700)
  ).current;

  useEffect(() => {
    getMbsMarketAlert().then((res) => {
      if (res?.data) {
        setMbsMarketAlertsData({
          increaseAlertEnabled: res.data.increaseAlertEnabled,
          increaseThreshold: res.data.increaseThreshold,
          decreaseAlertEnabled: res.data.decreaseAlertEnabled,
          decreaseThreshold: res.data.decreaseThreshold,
        })
      } else {
        setMbsMarketAlertsData(null);
      }
    })
  }, []);

  const handleUpdateMbsMarketAlert =
    <K extends keyof MbsMarketAlertForm>(key: K, value: MbsMarketAlertForm[K]) => {

      setMbsMarketAlertsData(prev => {
        if (!prev) return null;

        const updated = { ...prev, [key]: value };

        debouncedUpdate(updated);

        return updated;
      });
  }

  return (
    <Space direction="vertical" size={20}>
      <Typography.Title level={3} style={{ fontSize: 22, fontWeight: '400' }}>
        30-Year MBS Market Alerts
      </Typography.Title>

      <ContentCard loading={isGetMbsMarketAlertLoading}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography.Text style={{ marginRight: '6px' }}>
              Alert me via email when 30-year MBS increases more than
            </Typography.Text>
            <Space>
              <InputNumber
                min={1}
                max={1000}
                step={0.01}
                precision={2}
                value={mbsMarketAlertsData?.increaseThreshold}
                onChange={(value) => handleUpdateMbsMarketAlert('increaseThreshold', value || 15)}
                style={{ width: 80 }}
              />
              <Typography.Text style={{ width: "30px", minWidth: "30px", display: 'block' }}>bps</Typography.Text>
              <Switch
                checked={Boolean(mbsMarketAlertsData?.increaseAlertEnabled)}
                onChange={(checked) => handleUpdateMbsMarketAlert('increaseAlertEnabled', checked)}
              />
            </Space>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography.Text style={{ marginRight: '6px' }}>
              Alert me via email when 30-year MBS decreases more than
            </Typography.Text>
            <Space>
              <InputNumber
                min={1}
                max={1000}
                step={0.01}
                precision={2}
                value={mbsMarketAlertsData?.decreaseThreshold}
                onChange={(value) => handleUpdateMbsMarketAlert('decreaseThreshold', value || 15)}
                style={{ width: 80 }}
              />
              <Typography.Text style={{ width: "30px", minWidth: "30px", display: 'block' }}>bps</Typography.Text>
              <Switch
                checked={Boolean(mbsMarketAlertsData?.decreaseAlertEnabled)}
                onChange={(checked) => handleUpdateMbsMarketAlert('decreaseAlertEnabled', checked)}
              />
            </Space>
          </div>
        </Space>
      </ContentCard>
    </Space>
  )
}

export default MbsMarketAlerts
